import React from 'react';
import { Heart, Moon, Flame, TrendingUp, ClipboardList } from '@/utils/icons';
import { LucideIcon } from 'lucide-react';
import AthleteStatusCard from './AthleteStatusCard';

/** Shape of a single daily check-in record from the API. */
interface DailyCheckInEntry {
  check_in_date: string;
  soreness: number;
  motivation: number;
  mental_fatigue: number;
  session_rpe: number;
  notes: string;
  attended: boolean;
}

interface OverviewTabProps {
  athleteData: any;
  isDark: boolean;
  getValidatedStats: () => any;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ athleteData, isDark, getValidatedStats }) => {
  const validatedStats = getValidatedStats();

  // Derive the most recent daily check-in (if any)
  const checkins: DailyCheckInEntry[] = athleteData?.daily_checkins ?? [];
  const latestCheckIn = checkins.length > 0 ? checkins[checkins.length - 1] : null;

  return (
    <div className="space-y-6">
      {/* Unified Athlete Status: Readiness + Injury Risk */}
      <AthleteStatusCard
        athleteId={athleteData?.athlete_info?.id}
        isDark={isDark}
        defaultExpanded={true}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {(() => {
          return [
            {
              title: 'Recovery Score',
              value: validatedStats?.recovery.latest_recovery ? `${validatedStats.recovery.latest_recovery.toFixed(0)}%` : 'N/A',
              subtitle: `Avg: ${validatedStats?.recovery.average_recovery ? validatedStats.recovery.average_recovery.toFixed(0) : 'N/A'}%`,
              Icon: Heart
            },
            {
              title: 'Sleep Quality',
              value: validatedStats?.sleep.latest_quality ? `${validatedStats.sleep.latest_quality.toFixed(0)}%` : 'N/A',
              subtitle: `${validatedStats?.sleep.latest_duration ? validatedStats.sleep.latest_duration.toFixed(1) : 'N/A'}h duration`,
              Icon: Moon
            },
            {
              title: 'Daily Strain',
              value: validatedStats?.activity.latest_strain ? `${validatedStats.activity.latest_strain.toFixed(1)}` : 'N/A',
              subtitle: `Avg: ${validatedStats?.activity.average_strain ? validatedStats.activity.average_strain.toFixed(1) : 'N/A'}`,
              Icon: Flame
            },
            {
              title: 'Data Points',
              value: validatedStats?.recovery.data_points.toString() || '0',
              subtitle: `${athleteData.data_summary.analysis_period_days} day period`,
              Icon: TrendingUp
            }
          ];
        })().map((stat, index) => {
          const iconColors = ['cgm-icon-recovery', 'cgm-icon-sleep', 'cgm-icon-strain', 'cgm-icon-data'];
          return (
            <div
              key={stat.title}
              className={`cgm-metric-card`}
            >
              <div className="flex items-center gap-3 mb-4">
                <stat.Icon className={`w-8 h-8 flex-shrink-0 ${iconColors[index]}`} />
                <div className="text-sm cgm-text-secondary font-medium">{stat.title}</div>
              </div>
              <div className="text-2xl font-bold theme-text-primary mb-1">{stat.value}</div>
              <div className="text-sm theme-text-secondary">{stat.subtitle}</div>
            </div>
          );
        })}
      </div>

      {/* Daily Check-In Feedback Card */}
      {latestCheckIn && (
        <div className="cgm-card rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50">
          <div className="flex items-center gap-3 mb-4">
            <ClipboardList className="w-6 h-6 cgm-icon-data" />
            <h3 className="text-lg font-semibold theme-text-primary">
              Athlete Feedback
            </h3>
            <span className="ml-auto text-xs theme-text-secondary">
              {new Date(latestCheckIn.check_in_date + 'T12:00:00').toLocaleDateString(
                'en-US',
                { month: 'short', day: 'numeric' }
              )}
            </span>
          </div>

          {!latestCheckIn.attended ? (
            <p className="text-sm theme-text-secondary italic">
              Athlete reported they did not train on this day.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Soreness', value: latestCheckIn.soreness },
                { label: 'Motivation', value: latestCheckIn.motivation },
                { label: 'Mental Fatigue', value: latestCheckIn.mental_fatigue },
                { label: 'RPE', value: latestCheckIn.session_rpe },
              ].map((metric) => (
                <div key={metric.label} className="text-center">
                  <div className="text-2xl font-bold theme-text-primary">
                    {metric.value}
                    <span className="text-xs font-normal theme-text-secondary">
                      /10
                    </span>
                  </div>
                  <div className="text-xs theme-text-secondary mt-1">
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>
          )}

          {latestCheckIn.notes && (
            <p className="mt-4 text-sm theme-text-secondary italic border-t border-gray-200 dark:border-gray-700 pt-3">
              "{latestCheckIn.notes}"
            </p>
          )}

          {/* Mini trend: show last few check-ins as dots */}
          {checkins.length > 1 && (
            <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
              <p className="text-xs theme-text-secondary mb-2">
                RPE trend ({checkins.length} days)
              </p>
              <div className="flex items-end gap-1 h-8">
                {checkins.slice(-14).map((c, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-sm bg-[var(--accent-ui)]"
                    style={{
                      height: `${(c.session_rpe / 10) * 100}%`,
                      opacity: c.attended ? 1 : 0.25,
                    }}
                    title={`${c.check_in_date}: RPE ${c.session_rpe}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
